/**
 * PortfolioGallery — multi-image gallery editor for contractor portfolio items
 * Features:
 *   - Up to 6 images per project
 *   - Bulk upload (multi-file input)
 *   - Canvas auto-resize/compress to webp
 *   - Drag-to-reorder (drag over slots)
 *   - Delete individual images
 *   - Caption per image
 *   - First slot = cover photo (crown badge)
 *   - Progress indicator per image
 */
import React, { useRef, useState, useCallback } from 'react';
import { Crown, Plus, X, GripVertical, ImageIcon, Loader2, AlertCircle, Images } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 6;
const MAX_SIDE = 1400;     // max dimension after resize
const WEBP_QUALITY = 0.82;

/** Resolve expostand/ path or legacy http URL */
export function resolveGalleryUrl(path) {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API}/api/contractor/profile/assets/${path}`;
}

/** Compress file to webp via canvas */
async function compressToWebp(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const ratio = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width  = Math.round(img.width  * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas compression failed')), 'image/webp', WEBP_QUALITY);
    };
    img.onerror = reject;
    img.src = objectUrl;
  });
}

export default function PortfolioGallery({ images = [], onChange, entityId, disabled = false }) {
  const { token } = useAuth();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState({}); // { [localId]: progress 0-100 }
  const [errors, setErrors]     = useState([]);
  const [dragIdx, setDragIdx]   = useState(null);
  const [overIdx, setOverIdx]   = useState(null);

  // ── Upload single blob ────────────────────────────────────────────────────
  const uploadBlob = useCallback(async (blob, localId, originalName) => {
    setUploading(u => ({ ...u, [localId]: 10 }));
    const form = new FormData();
    const ext  = '.webp';
    form.append('file', blob, (originalName?.replace(/\.[^.]+$/, '') || 'image') + ext);
    let url = `${API}/api/contractor/profile/upload?asset_type=portfolio`;
    if (entityId) url += `&entity_id=${encodeURIComponent(entityId)}`;

    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    setUploading(u => ({ ...u, [localId]: 90 }));
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `Upload failed (${res.status})`);
    }
    const data = await res.json();
    setUploading(u => { const n = { ...u }; delete n[localId]; return n; });
    return data.asset_path;
  }, [entityId, token]);

  // ── Handle file selection / drop ──────────────────────────────────────────
  const handleFiles = useCallback(async (files) => {
    const errs = [];
    const remaining = MAX_IMAGES - images.length;
    const toUpload = Array.from(files).slice(0, remaining);

    if (toUpload.length === 0) {
      setErrors([`Maksimum ${MAX_IMAGES} resim yükleyebilirsiniz.`]);
      return;
    }

    for (const file of toUpload) {
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        errs.push(`${file.name}: Desteklenmeyen format. JPG, PNG, WEBP kullanın.`);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        errs.push(`${file.name}: Dosya çok büyük (maks. 20 MB).`);
        continue;
      }

      const localId = `local_${Date.now()}_${Math.random()}`;
      // Optimistic placeholder
      const previewUrl = URL.createObjectURL(file);
      onChange(prev => [...prev, { id: localId, url: previewUrl, caption: '', _isLocal: true }]);

      try {
        const blob     = await compressToWebp(file);
        const assetPath = await uploadBlob(blob, localId, file.name);
        URL.revokeObjectURL(previewUrl);
        onChange(prev => prev.map(img =>
          img.id === localId ? { ...img, url: assetPath, _isLocal: false } : img
        ));
      } catch (e) {
        URL.revokeObjectURL(previewUrl);
        onChange(prev => prev.filter(img => img.id !== localId));
        errs.push(`${file.name}: ${e.message}`);
      }
    }

    setErrors(errs);
    if (errs.length) setTimeout(() => setErrors([]), 5000);
  }, [images.length, onChange, uploadBlob]);

  // ── Drag-to-reorder ───────────────────────────────────────────────────────
  const onDragStart = (i) => { setDragIdx(i); };
  const onDragEnter = (i) => { if (i !== dragIdx) setOverIdx(i); };
  const onDragEnd   = ()  => {
    if (dragIdx !== null && overIdx !== null && dragIdx !== overIdx) {
      onChange(prev => {
        const arr = [...prev];
        const [moved] = arr.splice(dragIdx, 1);
        arr.splice(overIdx, 0, moved);
        return arr;
      });
    }
    setDragIdx(null); setOverIdx(null);
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const deleteImage = (id) => {
    onChange(prev => prev.filter(img => img.id !== id));
  };

  // ── Caption ───────────────────────────────────────────────────────────────
  const updateCaption = (id, caption) => {
    onChange(prev => prev.map(img => img.id === id ? { ...img, caption } : img));
  };

  const slots = [...images];
  const emptySlots = Math.max(0, Math.min(MAX_IMAGES - slots.length, MAX_IMAGES));

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Images className="w-4 h-4 text-gray-500" />
          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Proje Fotoğrafları
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${images.length >= MAX_IMAGES ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
            {images.length}/{MAX_IMAGES}
          </span>
        </div>
        {images.length < MAX_IMAGES && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {images.length === 0 ? 'Resim Yükle' : 'Daha Ekle'}
          </button>
        )}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-3 gap-3">
        {slots.map((img, i) => {
          const src = img._isLocal ? img.url : resolveGalleryUrl(img.url);
          const isUploading = !!uploading[img.id];
          const isDragging = dragIdx === i;
          const isOver = overIdx === i;
          return (
            <div
              key={img.id}
              draggable={!disabled && !isUploading}
              onDragStart={() => onDragStart(i)}
              onDragEnter={() => onDragEnter(i)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={onDragEnd}
              className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                isDragging ? 'opacity-40 scale-95' :
                isOver     ? 'border-green-400 scale-105 shadow-lg' :
                'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Image */}
              <div className="aspect-[4/3] bg-gray-100">
                {src ? (
                  <img src={src} alt={`Project ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Upload overlay */}
              {isUploading && (
                <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1">
                  <Loader2 className="w-5 h-5 text-white animate-spin" />
                  <span className="text-white text-xs">{uploading[img.id] || 0}%</span>
                </div>
              )}

              {/* Cover badge */}
              {i === 0 && !isUploading && (
                <div className="absolute top-1.5 left-1.5 bg-amber-400 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                  <Crown className="w-2.5 h-2.5" />Kapak
                </div>
              )}

              {/* Drag handle */}
              {!isUploading && (
                <div className="absolute top-1.5 right-6 text-white/70 cursor-grab">
                  <GripVertical className="w-3.5 h-3.5 drop-shadow" />
                </div>
              )}

              {/* Delete button */}
              {!isUploading && !disabled && (
                <button
                  type="button"
                  onClick={() => deleteImage(img.id)}
                  className="absolute top-1 right-1 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              )}

              {/* Caption */}
              <input
                type="text"
                disabled={disabled}
                value={img.caption || ''}
                onChange={(e) => updateCaption(img.id, e.target.value)}
                placeholder="Açıklama…"
                className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-2 py-1 placeholder-white/50 focus:outline-none focus:bg-black/80 w-full"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          );
        })}

        {/* Empty slots */}
        {Array.from({ length: emptySlots }).map((_, i) => (
          <button
            key={`empty_${i}`}
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className="aspect-[4/3] rounded-xl border-2 border-dashed border-gray-200 hover:border-green-400 hover:bg-green-50 flex flex-col items-center justify-center gap-1.5 transition-all group"
          >
            <Plus className="w-5 h-5 text-gray-300 group-hover:text-green-500 transition-colors" />
            {i === 0 && images.length === 0 && (
              <span className="text-xs text-gray-400">Toplu yükle</span>
            )}
          </button>
        ))}
      </div>

      {/* Hidden multi-file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }}
      />

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((err, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
              {err}
            </div>
          ))}
        </div>
      )}

      {/* Hint */}
      <p className="text-[10px] text-gray-400">
        Maks. {MAX_IMAGES} resim · JPG/PNG/WEBP · Otomatik sıkıştırılır · Sıralamak için sürükleyin · İlk resim kapak olarak kullanılır
      </p>
    </div>
  );
}
